import { observable, computed, reaction, makeObservable } from 'mobx'
import Document from './Document'
import { DocumentVersionDTO, ExtractionState } from './dtos/document-version'
import { Model } from 'infra/model'
import { DateZero, maybeParseDate } from 'utils/date-util'
import { Id } from 'types/definitions'
import { PrimeMember, GetMember } from 'iam/MemberStore'
import { GetDocument } from 'documents/DocumentStore'
import once from 'lodash/once'
import Member from 'iam/models/Member'

export default class DocumentVersion extends Model<
  DocumentVersionDTO,
  DocumentVersion
> {
  @observable id = Id.none
  @observable dateCreated = DateZero()
  @observable message = ''
  @observable authorId = ''
  @observable authorPublicId = ''
  @observable purged = false
  @observable files: Array<DocumentVersionFile> = []
  @observable documentId = Id.none
  @observable extractionState: ExtractionState = null
  @observable fields: Array<DocumentField> = []

  static get none() {
    return lazyNone()
  }

  constructor(
    private getDocument: GetDocument,
    private getMember: GetMember,
    primeMember: PrimeMember,
  ) {
    super()
    makeObservable(this)
    reaction(
      (): [Document | undefined, string, string] => [
        this.document,
        this.authorId,
        this.authorPublicId,
      ],
      ([doc, authorId, authorPublicId]) =>
        doc &&
        primeMember({
          workspace_id: doc.workspaceId,
          user_id: authorId,
          user_public_id: authorPublicId,
        }),
    )
  }

  @computed
  get document() {
    return this.getDocument(this.documentId)
  }

  @computed
  get author() {
    return (
      this.document && this.getMember(this.document.workspaceId, this.authorId)
    )
  }

  @computed
  get state() {
    return this.files[0].state
  }

  @computed
  get downloadable() {
    return (
      !this.document?.archived &&
      this.files &&
      this.files[0].state === 'Downloadable'
    )
  }

  @computed
  get isVerifyingClientData() {
    return (
      !this.document?.archived &&
      this.files &&
      this.files[0].state === 'VerifyingClientData'
    )
  }

  @computed
  get isManualVerificationRequired() {
    return (
      !this.document?.archived &&
      this.files &&
      this.files[0].state === 'ManualVerificationRequired'
    )
  }

  parse(json: DocumentVersionDTO): Partial<DocumentVersion> {
    return {
      id: json.id,
      documentId: json.document_id,
      dateCreated: maybeParseDate(json.date_created),
      message: json.message,
      authorId: json.author_id,
      authorPublicId: json.author_public_id,
      purged: json.purged,
      files: json.files.map((f) => ({
        id: f.id,
        dateCreated: new Date(f.date_created),
        fileName: f.file_name,
        state: f.state,
      })),
      extractionState: json.extraction_state,
      fields:
        json.fields?.map((attrs) => ({
          id: attrs.id,
          schemaId: attrs.schema_id,
          fileId: attrs.file_id,
          path: attrs.path,
          extractedValue: attrs.extracted_value,
          correctedValue: attrs.corrected_value,
        })) || [],
    }
  }
}

const lazyNone = once(
  () =>
    new DocumentVersion(
      () => Document.none,
      () => Member.none,
      () => Member.none,
    ),
)

export interface DocumentVersionFile {
  id: string
  dateCreated: Date
  fileName: string
  state:
    | 'Downloadable'
    | 'Purged'
    | 'Scanning'
    | 'Pending'
    | 'Failed'
    | 'VerifyingClientData'
    | 'ManualVerificationRequired'
}

interface DocumentField {
  id: string
  schemaId: string
  fileId: string
  path: string
  extractedValue?: unknown
  correctedValue?: unknown
}
